import { useCallback, useEffect } from 'react';

import Locator from '../../../../../shared/Locator';
import { useDresser } from '../../../../contexts/DrawerContext';
import ProjectDependencyDrawer from '../DependencyDrawer/ProjectDependencyDrawer';
import ReleaseGroupDependencyDrawer from '../DependencyDrawer/ReleaseGroupDependencyDrawer';
import type { DependencyScope } from '../../../../../shared/dependenciesV2';

type DependencyDrawerOptions = {
  // the dependency scope
  scope: DependencyScope | null;
  // the currently selected dependency locator
  selectedDependencyRevisionLocator: string | null;
  // callback for setting/unsetting the currently selected dependency locator
  setSelectedDependencyRevisionLocator: (locator: string | null) => void;
  // optional callback when dependency is deleted/ignored
  onDeleteProjectDependency: (locator: string) => void;
};

/**
 * Helper for displaying and hiding the dependency drawer, used in both managed
 * and vendored dependency views
 * @param opts the dependency drawer options
 */
export function useDependencyDrawer(opts: DependencyDrawerOptions) {
  const { scope, selectedDependencyRevisionLocator } = opts;
  const { setSelectedDependencyRevisionLocator, onDeleteProjectDependency } = opts;
  const { popDrawer, replaceDrawer } = useDresser();

  const onClose = useCallback(() => {
    setSelectedDependencyRevisionLocator(null);
  }, [setSelectedDependencyRevisionLocator]);

  useEffect(() => {
    if (selectedDependencyRevisionLocator) {
      if (scope?.scope === 'releaseGroup') {
        replaceDrawer(
          <ReleaseGroupDependencyDrawer
            onClose={onClose}
            releaseGroupId={scope.projectGroupId}
            releaseGroupReleaseId={scope.projectGroupReleaseId}
            dependencyRevisionLocator={selectedDependencyRevisionLocator}
            scope={{
              type: 'releaseGroup',
              releaseGroupId: scope.projectGroupId,
              releaseId: scope.projectGroupReleaseId,
            }}
          />,
          { onClose, shouldSkipClickOutside: true },
        );
      } else if (scope?.scope === 'project') {
        replaceDrawer(
          <ProjectDependencyDrawer
            onClose={onClose}
            projectRevisionLocator={scope.revision}
            dependencyRevisionLocator={selectedDependencyRevisionLocator}
            onDeleteDependency={onDeleteProjectDependency}
            scope={{
              type: 'project',
              projectLocator: Locator.parsePackageString(scope.revision),
              revisionLocator: scope.revision,
            }}
          />,
          { onClose, shouldSkipClickOutside: true },
        );
      }
    }
  }, [
    selectedDependencyRevisionLocator,
    scope,
    replaceDrawer,
    onDeleteProjectDependency,
    popDrawer,
    onClose,
  ]);
}
